import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { AuthLayout, Button, ResultCard } from "@core/components";
import { useLogout } from "@core/auth";
import { requireAuth } from "./-guards";

/**
 * `/no-access` — terminal landing for a signed-in user whose surfaces have no screen yet.
 *
 * TOP-LEVEL, not under `/staff`: it is the destination for surfaces that belong to no tree — only
 * `statistics` (observer) today (`intake_operations` joined the `/staff` tree 2026-09-13, see
 * `SURFACE_GROUP` in `@core/auth`). Parking it inside `/staff` would put it behind the very guard
 * that sends people here, so it would redirect to itself.
 *
 * `requireAuth`, not `requireGroup` — authentication is the only thing to assert. Any signed-in
 * user may see it whatever their bundles are; that is the point of the page.
 *
 * Rendered in `AuthLayout` rather than the app shell, because there is no navigation to offer:
 * the sidebar would be empty and every link would lead nowhere. Sign-out is the one real action,
 * so it is the one control — without it the user is stranded on a dead end.
 */
export const Route = createFileRoute("/no-access")({
  beforeLoad: ({ context, location }) =>
    requireAuth(context.queryClient, location.pathname),
  component: NoAccessScreen,
});

function NoAccessScreen() {
  const { t } = useTranslation("common");
  const logout = useLogout();

  return (
    <AuthLayout>
      <ResultCard
        tone="warning"
        icon="alert"
        title={t("access.none-title")}
        description={t("access.none-description")}
      >
        <Button
          fullWidth
          variant="secondary"
          onPress={() => {
            logout.mutate();
          }}
          isPending={logout.isPending}
          isDisabled={logout.isPending}
        >
          {t("layout.sign-out")}
        </Button>
      </ResultCard>
    </AuthLayout>
  );
}
